import { User } from '@/api/types/public';
import { ReportingCenter } from '@/reporting/reportingCenter';
import logger from '@/utils/client-logger';
import { ZL_USERINFO } from '@/utils/constants';
import { env } from '@/utils/env';
import { getPlatformLanguage } from '@/utils/locale-cookie';
import { isInZeloolApp } from '@/utils/tools';
import { Provider, ProviderType, TrackingEvent } from '../types';
import { syncSensorsIdentityToStorage } from '../utils/sensorsIdentity';

interface SensorsConfig {
  serverUrl: string;
  showLog: boolean;
  platform: string;
  platformName: string;
  envName: string;
  isEnabled?: boolean;
}

interface SnowplowIdentityProvider {
  setBusinessUserId: (userId: string) => void;
}

/**
 * 判断 provider 是否支持 Snowplow business user id 同步。
 */
function isSnowplowIdentityProvider(provider: unknown): provider is SnowplowIdentityProvider {
  return (
    typeof provider === 'object' &&
    provider !== null &&
    'setBusinessUserId' in provider &&
    typeof provider.setBusinessUserId === 'function'
  );
}

export class SensorsProvider implements Provider {
  type = ProviderType.SENSORS;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: SensorsConfig;

  constructor(config: SensorsConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // 动态加载神策脚本（如果尚未加载）
      if (!window.sensorsDataAnalytic201505) {
        await this.loadSensorsScript();
      }

      // Get the instance if it's already loaded
      const sensors = window.sensorsDataAnalytic201505;
      window.sensors = sensors;
      if (!sensors) {
        logger.error('Sensors Analytics SDK not loaded');
        return false;
      }

      // Initialize sensors with configuration
      sensors.init({
        server_url: this.config.serverUrl,
        app_js_bridge: isInZeloolApp(),
        is_track_single_page: true, // 单页面配置，默认开启，若页面中有锚点设计，需要将该配置删除，否则触发锚点会多触发 $pageview 事件
        use_client_time: true,
        send_type: 'beacon',
        heatmap: {
          // 是否开启点击图，default 表示开启，自动采集 $WebClick 事件，可以设置 'not_collect' 表示关闭。
          clickmap: 'default',
          // 是否开启触达图，not_collect 表示关闭，不会自动采集 $WebStay 事件，可以设置 'default' 表示开启。
          scroll_notice_map: 'default',
        },
        show_log: this.config.showLog,
        batch_send: {
          datasend_timeout: 6000, //一次请求超过多少毫秒的话自动取消，防止请求无响应。
          send_interval: 6000, //间隔多少毫秒发一次数据。
          storage_length: 200, // 存储 localStorage 条数最大值，默认：200 。如 localStorage 条数超过该值，则使用 image 方式立即发送数据。v1.24.8 以上支持。
        },
      });

      // Register common properties
      this.registerCommonProperties(sensors);

      // Setup additional features
      sensors.use('PageLeave', {});
      sensors.quick('autoTrack');
      this.syncIdentity();

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Sensors Analytics:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      const sensors = window.sensorsDataAnalytic201505;
      try {
        const _params: Record<string, any> = {
          $url: window.location.href,
        };
        // 根据标准：只要是值为 '', null, undefined, 'null', 'undefined'
        Object.keys(event.properties).forEach((key) => {
          const v = event.properties[key];
          if (
            v !== '' &&
            v !== 'null' &&
            v !== 'undefined' &&
            v !== null &&
            v !== undefined &&
            !(v instanceof Array && !v.length)
          ) {
            _params[key] = v;
          }
        });
        // 神策自动上报链路禁止 event_id，使用 sensors_event_id 承载内部统一事件 ID。
        if (event.eventId) {
          _params.sensors_event_id = event.eventId;
        }
        if (this.loggingEnabled) {
          logger.log('sensorProvider, Event:', event.eventName, _params);
        }
        sensors.track(event.eventName, _params);
      } catch (error) {
        logger.log('sensors track error', error);
      }
    } catch (error) {
      logger.error('Sensors tracking error:', error);
    }
  }

  sensorsLogout() {
    try {
      // window.sensorsDataAnalytic201505.logout();
      window.sensorsDataAnalytic201505.setProfile({ pub_is_login: false });
    } catch (error) {
      logger.log('sensors logout error', error);
    }
  }

  /**
   * 调用神策登录，并同步 Snowplow business user id。
   */
  sensorsLogin(uuid: string, snowplowUserId?: string) {
    try {
      if (uuid) {
        window.sensorsDataAnalytic201505.login(uuid);
        this.syncIdentity();
        this.syncSnowplowBusinessUserId(snowplowUserId);
      }
    } catch (error) {
      logger.log('sensors login error', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 同步 Snowplow business user id，失败不阻断神策登录链路。
   */
  private syncSnowplowBusinessUserId(userId?: string): void {
    if (!userId) return;

    try {
      const snowplowProvider = ReportingCenter.getInstance().getProvider(ProviderType.SNOWPLOW);
      if (isSnowplowIdentityProvider(snowplowProvider)) {
        snowplowProvider.setBusinessUserId(userId);
      }
    } catch (error) {
      logger.log('snowplow set business user id error', error);
    }
  }

  /**
   * 将神策 SDK 当前身份同步到本地缓存，供 Snowplow 首屏事件兜底读取。
   */
  private syncIdentity(): void {
    syncSensorsIdentityToStorage();
  }

  /**
   * 动态加载神策脚本
   */
  private loadSensorsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://common.xmslol.com/libs/sensorsdata@1.26.18/web/sensorsdata.js';

        script.onload = () => {
          // 等待全局对象初始化
          resolve();
        };

        script.onerror = () => {
          const error = 'Failed to load Sensors Analytics script';
          logger.error('Sensors:', error);
          reject(new Error(error));
        };

        document.head.appendChild(script);
      } catch (error) {
        logger.error('Sensors: Error creating script element:', error);
        reject(error);
      }
    });
  }

  private registerCommonProperties(sensors: any): void {
    // const latestChannelAttribute = this.getLatestChannelAttribute();
    const params = {
      pub_platform: this.config.platform,
      pub_platform_name: this.config.platformName,
      pub_platform_language: () => getPlatformLanguage(),
      pub_client: this.config.platform,
      pub_env_name: () => {
        // 区分灰度的新服务还是旧服务
        if (this.config.envName === 'production') return 'production_is_nextjs';
        return this.config.envName;
      },
      pub_member_level: () => {
        let userInfo: User = {};
        try {
          userInfo = JSON.parse(localStorage.getItem(ZL_USERINFO) || '{}') || {};
        } catch (error) {
          logger.error('Failed to parse user info:', error);
        }
        return `${userInfo.member_level >= -1 ? userInfo.member_level : '9999'}`;
      },
      pub_is_paid: () => {
        let userInfo: User = {};
        try {
          userInfo = JSON.parse(localStorage.getItem(ZL_USERINFO) || '{}') || {};
        } catch (error) {
          logger.error('Failed to parse user info:', error);
        }
        return !!userInfo.last_pay_time;
      },
      pub_is_login: () => {
        try {
          return !!localStorage.getItem('zl-token');
        } catch (error) {
          logger.error('Failed to parse user info:', error);
          return false;
        }
      },
      pub_zl_utm_source: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_source || undefined;
        }
        return undefined;
      },
      pub_zl_utm_medium: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_medium || undefined;
        }
        return undefined;
      },
      pub_zl_utm_campaign: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_campaign || undefined;
        }
        return undefined;
      },
      pub_zl_utm_campaignid: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_campaignid || undefined;
        }
        return undefined;
      },
      pub_zl_referrer: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_referrer || undefined;
        }
        return undefined;
      },
      pub_zl_page: () => {
        if (
          window.latestChannelAttribute?.expires > Date.now() ||
          window.latestChannelAttribute?.expires === -1
        ) {
          return window.latestChannelAttribute?.pub_zl_page || undefined;
        }
        return undefined;
      },
      pub_landing_page: () => {
        return window.latestChannelAttribute?.pub_landing_page || undefined;
      },
    };
    for (const key in params) {
      // 虽说不再报警告了但是值失去了实时性
      // if (typeof params[key] === 'function') {
      //   params[key] = params[key]();
      // }
      if (params[key] === undefined || params[key] === null || params[key] === '') {
        delete params[key];
      }
    }
    sensors.registerPage(params);
  }

  /**
   * 启用Sensors日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Sensors日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }
}
